import React, { useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';

// A record's product line points at exactly one of a real Product or a
// generic ProductGroup (RecordProduct.ProductId/ProductGroupId) — imports
// match a too-vague product name to a group. `name` is the display label;
// groups carry a " (generic)" suffix, same as RecordProductDto.ProductName.
export interface ProductOrGroupSelection {
  kind: 'product' | 'group';
  id: string;
  name: string;
}

const GENERIC_SUFFIX = ' (generic)';

interface ProductOrGroupAutocompleteProps {
  authHeader: { Authorization: string };
  productId: string | null;
  productGroupId: string | null;
  name: string;
  onChange: (selection: ProductOrGroupSelection | null) => void;
}

// Debounced search against both the paged /api/products endpoint (1500+
// rows — too many to load as a plain dropdown) and /api/product-groups,
// shown as two option groups (generic groups first).
const ProductOrGroupAutocomplete: React.FC<ProductOrGroupAutocompleteProps> = ({ authHeader, productId, productGroupId, name, onChange }) => {
  const [inputValue, setInputValue] = useState(name);
  const [options, setOptions] = useState<ProductOrGroupSelection[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setInputValue(name);
  }, [productId, productGroupId, name]);

  useEffect(() => {
    // A selected group's label ends in " (generic)", which no name matches.
    const term = inputValue.trim().replace(/\s*\(generic\)$/i, '');
    if (term.length < 2) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timeout = setTimeout(() => {
      const q = encodeURIComponent(term);
      const fetchItems = (url: string) =>
        fetch(url, { headers: authHeader })
          .then((res) => (res.ok ? res.json() : { items: [] }))
          .then((data: { items: { id: string; name: string }[] }) => data.items)
          .catch(() => [] as { id: string; name: string }[]);
      Promise.all([fetchItems(`/api/products?search=${q}&pageSize=25`), fetchItems(`/api/product-groups?search=${q}&pageSize=10`)])
        .then(([products, groups]) => {
          if (cancelled) return;
          // Groups first — there are few of them, and a long product list
          // would otherwise push the generic match out of view.
          setOptions([
            ...groups.map((g) => ({ kind: 'group' as const, id: g.id, name: `${g.name}${GENERIC_SUFFIX}` })),
            ...products.map((p) => ({ kind: 'product' as const, id: p.id, name: p.name })),
          ]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const selected: ProductOrGroupSelection | null = productId
    ? { kind: 'product', id: productId, name }
    : productGroupId
      ? { kind: 'group', id: productGroupId, name }
      : null;

  return (
    <Autocomplete
      size="small"
      sx={{ flexGrow: 1, minWidth: 160 }}
      options={options}
      loading={loading}
      value={selected}
      inputValue={inputValue}
      onInputChange={(_, value) => setInputValue(value)}
      onChange={(_, value) => onChange(value)}
      groupBy={(o) => (o.kind === 'product' ? 'Products' : 'Generic product groups')}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.kind === b.kind && a.id === b.id}
      filterOptions={(x) => x}
      renderInput={(params) => <TextField {...params} label="Product" placeholder="Search products or generic groups…" />}
    />
  );
};

export default ProductOrGroupAutocomplete;
